import { Link, useParams } from 'react-router-dom'
import { ErrorBanner, SeverityPill, formatUtc, useAsync } from '../../components/Async'
import { api } from '../../lib/api'
import { AccountSummary } from './AccountSummary'
import { PromptPanel } from './PromptPanel'

/**
 * One event, and the first screen in this console to render the OCSF attribute bag.
 *
 * The bag has been written and indexed since Phase 5 and shown nowhere, which meant a capability could
 * report something perfectly and an operator still had no way to read it — the account and plan an
 * assistant was signed in to spent a day in that state. Everything the server did not promote to a
 * column lives here, so this page shows the named things it understands and then the rest verbatim
 * rather than deciding on the operator's behalf which of it matters.
 */
export function EventDetailPage() {
  const { eventRecordId = '' } = useParams()
  const event = useAsync(() => api.getEvent(eventRecordId), [eventRecordId])

  const detail = event.value
  const attributes: Record<string, unknown> = (() => {
    if (!detail) return {}
    try {
      return JSON.parse(detail.attributesJson) as Record<string, unknown>
    } catch {
      return {}
    }
  })()

  return (
    <>
      <div className="page-header">
        <div>
          <h1 data-testid="event-title">{detail?.summary.className ?? 'Event'}</h1>
          {detail && (
            <p>
              <SeverityPill severityId={detail.summary.severityId} />{' '}
              · {detail.summary.eventType} · {formatUtc(detail.summary.occurredUtc)} on{' '}
              {detail.summary.deviceName}
            </p>
          )}
        </div>
        <Link className="button" to="/events">
          Back to events
        </Link>
      </div>

      <ErrorBanner message={event.error} />

      {detail && (
        <div className="editor-grid">
          <div>
            <div className="card">
              <div className="card-body">
                <h3 style={{ marginTop: 0 }}>What happened</h3>
                <p>{detail.summary.message ?? 'No message was recorded.'}</p>

                <dl className="detail">
                  <dt>Capability</dt>
                  <dd>{detail.summary.capability ?? 'none'}</dd>
                  <dt>Assistant</dt>
                  <dd>{detail.agentId ?? 'not recorded'}</dd>
                  <dt>Process</dt>
                  <dd>{detail.processName ?? 'not recorded'}</dd>
                  <dt>Signed-in user</dt>
                  <dd>{detail.principalUser ?? 'not recorded'}</dd>
                  <dt>Rules</dt>
                  <dd>{detail.summary.matchedRuleNames || 'none matched'}</dd>
                  <dt>Received</dt>
                  <dd>{formatUtc(detail.summary.receivedUtc)}</dd>
                  <dt>Device</dt>
                  <dd>
                    <Link to={`/devices/${detail.summary.deviceId}`} data-testid="event-device-link">
                      {detail.summary.deviceName}
                    </Link>
                  </dd>
                </dl>
              </div>
            </div>

            <PromptPanel eventRecordId={eventRecordId} />

            {/* The rest of the bag, verbatim. Named panels above cover what this console understands;
                this is what keeps a capability the console has never heard of readable at all. */}
            <div className="card">
              <div className="card-body">
                <h3 style={{ marginTop: 0 }}>Everything else this event carries</h3>
                {Object.keys(attributes).length === 0 ? (
                  <div className="empty">This event carries no attributes.</div>
                ) : (
                  <pre className="prompt-text" data-testid="event-attributes">
                    {JSON.stringify(attributes, null, 2)}
                  </pre>
                )}
              </div>
            </div>
          </div>

          <div>
            <AccountSummary attributesJson={detail.attributesJson} />
          </div>
        </div>
      )}

      {event.loading && !detail && <div className="empty">Loading…</div>}
    </>
  )
}
